import { useSearchParams } from 'react-router-dom';
import TabBar from '../components/filtering/TabBar';
import PageLayout from '../components/global/PageLayout';
import { TabInt } from '../interfaces';
import BusinessSettings from '../components/businesses/BusinessSettings';
import BookingSettings from '../components/businesses/BookingSettings';
import ClientMessagesSettings from '../components/businesses/ClientMessagesSettings';
import useUser from '../hooks/global/useUser';
import BusinessTrips from '../components/businesses/BusinessTrips';
import { isUserAdminOrManager } from '../utils/global/helper';
import OnboardingBanner from '../components/onboarding/OnboardingBanner';
import { canIndependentManageShop } from '../helpers/businesses';
import TaxDiscountsSettings from '../components/businesses/TaxDiscountsSettings';
import TerminalSettings from '../components/businesses/TerminalSettings';
import ServicesSettings from '../components/businesses/ServicesSettings';

export enum ShopTab {
  BUSINESS = 'business',
  BOOKING = 'booking',
  CLIENT_MESSAGES = 'client-messages',
  BUSINESS_TRIPS = 'business-trips',
  TAX_DISCOUNTS = 'tax-discounts',
  TERMINAL = 'terminal',
  SERVICES = 'services',
}

export default function ShopPage() {
  const { user } = useUser();

  const showTripsTab =
    user?.business.id === 'clxewj8iv000i12faik0kw5yu' ||
    user?.business.id === 'clxcgn8nk005ys38jv7xax9hf';
  const isStudio = user?.business?.type === 'STUDIO';
  const showTaxTab =
    user?.business?.type === 'INDEPENDENT' || isUserAdminOrManager(user!);
  const showDiscountsTab = isStudio && isUserAdminOrManager(user);
  const showTerminalTab =
    user?.business?.type === 'INDEPENDENT' || isUserAdminOrManager(user!);

  const tabs = [
    {
      id: ShopTab.BUSINESS,
      label: 'Business',
      component: <BusinessSettings />,
      hidden: false,
    },
    {
      id: ShopTab.BOOKING,
      label: 'Booking',
      component: <BookingSettings />,
      hidden: !isStudio,
    },
    {
      id: ShopTab.SERVICES,
      label: 'Services',
      component: <ServicesSettings />,
      hidden: isStudio,
    },
    {
      id: ShopTab.CLIENT_MESSAGES,
      label: 'Client Messages',
      component: <ClientMessagesSettings />,
      hidden: !isStudio,
    },
    {
      id: ShopTab.BUSINESS_TRIPS,
      label: 'Business Trips',
      component: <BusinessTrips />,
      hidden: !showTripsTab,
    },
    {
      id: ShopTab.TAX_DISCOUNTS,
      label:
        showTaxTab && showDiscountsTab
          ? 'Tax & Discounts'
          : showTaxTab
          ? 'Sales Tax'
          : 'Discounts',
      component: <TaxDiscountsSettings />,
      hidden: !showTaxTab && !showDiscountsTab,
    },
    {
      id: ShopTab.TERMINAL,
      label: 'Terminal',
      component: <TerminalSettings />,
      hidden: !showTerminalTab,
    },
  ] as const satisfies TabInt[];
  const filteredTabs = tabs.filter(tab => !tab.hidden);

  const [params, setParams] = useSearchParams();
  const firstTab = filteredTabs[0];
  const paramTab = params.get('tab') as ShopTab | null;
  const selectedTab = paramTab
    ? filteredTabs.find(tab => tab.id === paramTab)?.id || firstTab?.id
    : firstTab?.id;

  const selectedTabIndex = Math.max(
    filteredTabs.findIndex(tab => tab.id === selectedTab),
    0,
  );
  const setSelectedTabIndex = (index: number) => {
    const newTab = filteredTabs[index]?.id || firstTab?.id;
    const newParams = new URLSearchParams(params);
    newParams.set('tab', newTab);
    setParams(newParams);
  };

  const onboardingStep =
    !selectedTab || selectedTab === ShopTab.BUSINESS
      ? 'LOCATION'
      : selectedTab === ShopTab.BOOKING
      ? 'SHOP_SETTINGS'
      : selectedTab === ShopTab.CLIENT_MESSAGES
      ? 'BOOKING_MESSAGES'
      : selectedTab === ShopTab.TERMINAL
      ? 'TERMINAL_READER'
      : null;

  return (
    <PageLayout
      title="Business Settings"
      guard={{
        isAllowed:
          !!user &&
          (isUserAdminOrManager(user) || canIndependentManageShop(user)),
      }}
      topSection={<OnboardingBanner step={onboardingStep} />}>
      <TabBar
        tabs={tabs}
        currentTab={selectedTabIndex}
        changeTab={setSelectedTabIndex}
      />
    </PageLayout>
  );
}
